/**
 * CORE-AQ (Core-100 R4) — a person corrects which ticket a call belongs to.
 *
 * Automatic call routing can attach a call to the wrong customer or visit. The one server authority for the
 * correction is `relink_call_event` (R4-12): front office only, a reason is required, the old link is kept in
 * `call_event_link_corrections`, the call is posted on the corrected ticket's thread, and a later provider callback
 * cannot undo it. Until now that authority had no screen (RPC only). This panel is that screen: the advisor names the
 * correct ticket by the number they see on it, says why, and the server re-derives the customer and vehicle from that
 * ticket. Nothing is changed in the browser; a refusal is said in words.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';

interface Props {
  callEventId: string;
  /** The ticket the call is on now (it cannot be "moved" to itself). */
  currentTicketId: string | null;
}

/** The readable number a person types (`simple_ticket_number`) or a ticket id → the ticket id, within the user's reach (RLS). */
export async function resolveTicketForRelink(entry: string): Promise<string | null> {
  const v = entry.trim().replace(/^#/, '');
  if (!v) return null;
  const query = supabase.from('tickets').select('id').is('deleted_at', null).limit(2);
  const { data, error } = /^\d+$/.test(v)
    ? await query.eq('simple_ticket_number', Number(v))
    : await query.eq('id', v);
  if (error) throw new Error('lookup');
  const rows = (data ?? []) as Array<{ id: string }>;
  return rows.length === 1 ? rows[0].id : null;
}

export function relinkRefusalMessage(message: string): string {
  if (/CALL_RELINK_REASON_REQUIRED/.test(message)) return 'Say why the call is being moved.';
  if (/CALL_RELINK_FORBIDDEN/.test(message)) return 'Only the front office can move a call.';
  if (/CALL_RELINK_NOT_IN_SHOP/.test(message)) return 'That ticket is not a ticket of this shop.';
  if (/CALL_RELINK_INCONSISTENT/.test(message)) return 'That ticket belongs to a different customer or vehicle.';
  return 'The call could not be moved.';
}

export function CallRelink({ callEventId, currentTicketId }: Props) {
  const [open, setOpen] = useState(false);
  const [ticketEntry, setTicketEntry] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setBusy(true);
    setMessage(null);
    let ticketId: string | null = null;
    try {
      ticketId = await resolveTicketForRelink(ticketEntry);
    } catch {
      setBusy(false);
      setMessage('The ticket could not be looked up. Nothing was changed.');
      return;
    }
    if (!ticketId) {
      setBusy(false);
      setMessage('No single ticket has that number. Nothing was changed.');
      return;
    }
    if (ticketId === currentTicketId) {
      setBusy(false);
      setMessage('The call is already on this ticket.');
      return;
    }
    const { error } = await supabase.rpc('relink_call_event' as never, {
      p_call_event_id: callEventId,
      p_customer_id: null,
      p_ticket_id: ticketId,
      p_vehicle_id: null,
      p_reason: reason.trim(),
    } as never);
    setBusy(false);
    if (error) {
      setMessage(relinkRefusalMessage(error.message));
      return;
    }
    setDone(true);
    setOpen(false);
  };

  if (done) {
    return <p className="mt-1 text-xs font-medium" data-testid="call-relinked">Call moved to the corrected ticket (reason recorded).</p>;
  }
  if (!open) {
    return (
      <Button size="sm" variant="ghost" className="mt-1 h-7 px-2 text-xs" onClick={() => setOpen(true)} data-testid="call-relink-open">
        Wrong ticket? Move this call
      </Button>
    );
  }
  return (
    <div className="mt-2 rounded-md border p-2 text-xs" data-testid="call-relink">
      <Input
        aria-label="Correct ticket number"
        placeholder="Ticket number"
        value={ticketEntry}
        onChange={(e) => setTicketEntry(e.target.value)}
        className="h-8 text-xs"
      />
      <Textarea
        aria-label="Reason for moving the call"
        placeholder="Why does this call belong to that ticket?"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className="mt-1 h-12 text-xs"
      />
      <div className="mt-1 flex gap-2">
        <Button size="sm" disabled={busy || !ticketEntry.trim() || !reason.trim()} onClick={() => void submit()}>
          Move call
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
      </div>
      {message && <p className="mt-1 text-red-700" role="alert">{message}</p>}
    </div>
  );
}

/** The relink action is front office only (the same role set `relink_call_event` admits); nothing renders for others. */
export function FrontOfficeCallRelink(props: Props) {
  const { canViewTicketPII: isFrontOffice } = usePermissions();
  return isFrontOffice ? <CallRelink {...props} /> : null;
}
